import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import type {
  PriceVarianceFilters,
  VarianceGroupedSort,
  VarianceGroupRow,
  VarianceSort,
} from "../../api/reports";
import type { PriceAnomalyRuleSnapshot } from "../../api/priceAnomalyRule";

import type { DeliveryKind, GroupMode, PriceVariancePreset, PriceVarianceView } from "./types";
import type { AnalysisPeriod } from "./analysisPeriod";
import {
  DEFAULT_MIN_DELTA_PCT,
  deliveryKindFor,
  parsePriceVarianceUrlState,
  serializePriceVarianceUrlState,
  SPIKE_MIN_DELTA_PCT,
  SPIKE_MIN_REFERENCE_COUNT,
} from "./urlState";
import type { PriceVarianceUrlState } from "./urlState";

export function usePriceVarianceReportState(
  effectiveRule?: PriceAnomalyRuleSnapshot,
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const ruleDefaults = useMemo(
    () => ({
      minDeltaPct: Number(effectiveRule?.threshold_pct ?? DEFAULT_MIN_DELTA_PCT),
      minOverpayAbs:
        effectiveRule?.minimum_overpay_abs == null
          ? null
          : Number(effectiveRule.minimum_overpay_abs),
    }),
    [effectiveRule],
  );
  const state = useMemo(
    () => parsePriceVarianceUrlState(searchParams, ruleDefaults),
    [ruleDefaults, searchParams],
  );
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const deliveryKind = deliveryKindFor(state.filters.is_warehouse);
  const effectiveFilters = useMemo<PriceVarianceFilters>(
    () => ({
      ...state.filters,
      ...(state.preset === "historical_spikes"
        ? { positive_only: true }
        : {}),
    }),
    [state.filters, state.preset],
  );

  function writeState(next: PriceVarianceUrlState) {
    setSearchParams(serializePriceVarianceUrlState(next), { replace: true });
  }

  function filtersWithUpdate<K extends keyof PriceVarianceFilters>(
    key: K,
    value: PriceVarianceFilters[K] | undefined,
  ): PriceVarianceFilters {
    const nextFilters = { ...state.filters };
    if (
      value === undefined ||
      value === null ||
      value === "" ||
      (Array.isArray(value) && value.length === 0)
    ) {
      delete nextFilters[key];
    } else {
      nextFilters[key] = value;
    }
    return nextFilters;
  }

  function updateFilter<K extends keyof PriceVarianceFilters>(
    key: K,
    value: PriceVarianceFilters[K] | undefined,
  ) {
    writeState({ ...state, filters: filtersWithUpdate(key, value) });
  }

  function setPreset(nextPreset: PriceVariancePreset | null) {
    const nextFilters = { ...state.filters };
    if (nextPreset === "historical_spikes") {
      nextFilters.min_delta_pct = SPIKE_MIN_DELTA_PCT;
      nextFilters.min_reference_count = SPIKE_MIN_REFERENCE_COUNT;
      delete nextFilters.price_anomaly_status;
    } else {
      nextFilters.min_delta_pct = ruleDefaults.minDeltaPct;
      if (ruleDefaults.minOverpayAbs == null) {
        delete nextFilters.min_overpay_abs;
      } else {
        nextFilters.min_overpay_abs = ruleDefaults.minOverpayAbs;
      }
      nextFilters.price_anomaly_status = "open";
      delete nextFilters.min_reference_count;
    }
    writeState({
      ...state,
      preset: nextPreset,
      filters: nextFilters,
      sort: nextPreset === "historical_spikes" ? "delta_pct_desc" : "overpay_desc",
    });
  }

  function setSort(next: VarianceSort) {
    writeState({ ...state, sort: next });
  }

  function setGroupedSort(next: VarianceGroupedSort) {
    writeState({ ...state, groupedSort: next });
  }

  function changeGroupMode(next: GroupMode) {
    writeState({ ...state, groupMode: next });
    setExpanded(new Set());
  }

  function setDeliveryKind(next: DeliveryKind) {
    const nextFilters = { ...state.filters };
    if (next === "lager") {
      nextFilters.is_warehouse = true;
    } else if (next === "baustellen") {
      nextFilters.is_warehouse = false;
    } else {
      delete nextFilters.is_warehouse;
    }
    writeState({ ...state, filters: nextFilters });
  }

  function resetAll() {
    // Zurücksetzen bleibt im Reiter „Positionen“; ohne view öffnet die Top 10 (#630).
    setSearchParams(new URLSearchParams({ view: "positions" }), { replace: true });
    setExpanded(new Set());
  }

  /** Analyse: alle Mehrkosten der letzten 12 Monate, im aktuellen Reiter. */
  function analysisDefaults(
    view: PriceVarianceView,
    keep: PriceVarianceFilters = {},
    layout: Partial<Pick<PriceVarianceUrlState, "sort" | "groupedSort" | "groupMode" | "preset">> = {},
  ): PriceVarianceUrlState {
    return {
      preset: layout.preset ?? null,
      view,
      period: keep.from || keep.to ? "custom" : "12m",
      filters: { ...keep, reference_quality: keep.reference_quality ?? "sufficient", positive_only: true },
      sort: layout.sort ?? "overpay_desc",
      groupedSort: layout.groupedSort ?? "overpay_desc",
      groupMode: layout.groupMode ?? "none",
    };
  }

  /**
   * Analyse defaults. An old link keeps its own filters and, when it brings
   * them, its list layout (grouping, sort); "Filter zurücksetzen" passes none.
   */
  function applyAnalysisDefaults(
    keep: PriceVarianceFilters = {},
    layout: Partial<Pick<PriceVarianceUrlState, "sort" | "groupedSort" | "groupMode" | "preset">> = {},
    view: PriceVarianceView = state.view,
  ) {
    writeState(analysisDefaults(view, keep, layout));
    setExpanded(new Set());
  }

  /** An old positions link: same filters, shown as "Alle Mehrkosten". */
  function adoptLegacyList() {
    const hasView = searchParams.has("view");
    writeState({
      ...state,
      view: hasView ? state.view : "positions",
      period: state.filters.from || state.filters.to ? "custom" : "all",
    });
  }

  function setView(view: PriceVarianceView) {
    writeState({ ...state, view });
  }

  function setPeriod(period: AnalysisPeriod) {
    const nextFilters = { ...state.filters };
    // A named period resolves itself; "Eigener Zeitraum" keeps the dates.
    if (period !== "custom") {
      delete nextFilters.from;
      delete nextFilters.to;
    }
    writeState({ ...state, period: period === "all" ? "all" : period, filters: nextFilters });
  }

  function toggleGroupRow(groupKey: string) {
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  }

  function groupKeyFor(row: VarianceGroupRow): string {
    return [
      row.manufacturer_id ?? "∅",
      row.project_key ?? "∅",
      row.delivery_address_key ?? "∅",
    ].join("|");
  }

  return {
    preset: state.preset,
    setPreset,
    filters: state.filters,
    effectiveFilters,
    sort: state.sort,
    setSort,
    groupedSort: state.groupedSort,
    setGroupedSort,
    groupMode: state.groupMode,
    changeGroupMode,
    deliveryKind,
    setDeliveryKind,
    expanded,
    updateFilter,
    resetAll,
    view: state.view,
    setView,
    period: state.period,
    setPeriod,
    applyAnalysisDefaults,
    adoptLegacyList,
    toggleGroupRow,
    groupKeyFor,
  };
}
